import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '../../primitives/button.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../primitives/select.js';
import { PAGE_SIZE_OPTIONS, pageCount } from '../list-state.js';
import type { ListState } from '../types.js';

export interface PaginationBarProps {
  state: ListState;
  total: number;
  onChange: (patch: Partial<ListState>) => void;
}

export function PaginationBar({ state, total, onChange }: PaginationBarProps) {
  const pages = pageCount(total, state.pageSize);
  const first = total === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
  const last = Math.min(state.page * state.pageSize, total);

  return (
    <div
      data-slot="pagination-bar"
      className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-[var(--cell-padding-x)] py-2"
    >
      <p className="text-secondary text-muted" aria-live="polite">
        {total === 0 ? 'Nessun risultato' : `${String(first)}–${String(last)} di ${String(total)}`}
      </p>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-secondary text-muted">Righe</span>
          <Select
            value={String(state.pageSize)}
            onValueChange={(value) => onChange({ pageSize: Number(value), page: 1 })}
          >
            <SelectTrigger size="sm" className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Prima pagina"
            disabled={state.page <= 1}
            onClick={() => onChange({ page: 1 })}
          >
            <ChevronsLeft />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Pagina precedente"
            disabled={state.page <= 1}
            onClick={() => onChange({ page: state.page - 1 })}
          >
            <ChevronLeft />
          </Button>
          <span className="px-2 text-secondary text-muted">
            {String(state.page)} / {String(pages)}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Pagina successiva"
            disabled={state.page >= pages}
            onClick={() => onChange({ page: state.page + 1 })}
          >
            <ChevronRight />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Ultima pagina"
            disabled={state.page >= pages}
            onClick={() => onChange({ page: pages })}
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
